import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Loader2, MessageSquare, Eye, Edit } from "lucide-react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useToast } from "@/hooks/use-toast";
import { getFeedback, updateFeedbackStatus, FeedbackItem, GetFeedbackParams } from "@/data-access/feedback";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function FeedbackTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "reviewed">("all");
  const [sortBy, setSortBy] = useState<"createdAt" | "status">("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryParams: GetFeedbackParams = {
    page,
    limit: 10,
    sortBy,
    sortOrder,
    ...(statusFilter !== "all" && { status: statusFilter }),
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['feedback', page, statusFilter, sortBy, sortOrder],
    queryFn: () => getFeedback(queryParams),
    placeholderData: (previousData) => previousData,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "pending" | "reviewed" }) =>
      updateFeedbackStatus(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      toast({
        title: "Status updated",
        description: "Feedback status has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSort = (field: "createdAt" | "status") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleStatusChange = (feedbackId: number, newStatus: "pending" | "reviewed") => {
    updateStatusMutation.mutate({ id: feedbackId, status: newStatus });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateMessage = (message: string, maxLength: number = 100) => {
    return message.length > maxLength ? `${message.substring(0, maxLength)}...` : message;
  };

  return (
    <div className="space-y-6 container mx-auto max-w-screen-lg">
      {/* Stats Cards */}
      <div className="flex justify-center gap-4 w-full">
        <Card className="w-[300px]">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Feedback</p>
              <p className="text-2xl font-bold">{data?.totalCount ?? 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="w-[300px]">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Pending Review</p>
              <p className="text-2xl font-bold text-orange-600">
                {data?.feedback.filter(f => f.status === 'pending').length ?? 0}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <Select
          value={statusFilter}
          onValueChange={(value: "all" | "pending" | "reviewed") => {
            setStatusFilter(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-48 bg-background border border-input h-12 rounded-lg">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Error Display */}
      {error instanceof Error && (
        <div className="text-red-500">
          {error.message}
        </div>
      )}

      {/* Feedback Table */}
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        )}
        
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">User</TableHead>
              <TableHead className="whitespace-nowrap">Message</TableHead>
              <TableHead 
                className="cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('status')}
              >
                Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead 
                className="cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('createdAt')}
              >
                Created At {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead className="whitespace-nowrap">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.feedback.map((feedback) => (
              <TableRow key={feedback.id}>
                <TableCell className="whitespace-nowrap">
                  <div>
                    <div className="font-medium">{feedback.user.username}</div>
                    <div className="text-xs text-muted-foreground">{feedback.user.email}</div>
                  </div>
                </TableCell>
                <TableCell className="max-w-xs">
                  <div 
                    className="cursor-pointer hover:text-primary break-words"
                    onClick={() => setSelectedFeedback(feedback)}
                    title="Click to view full message"
                  >
                    {truncateMessage(feedback.message)}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <StatusCell 
                    feedback={feedback} 
                    onStatusChange={handleStatusChange}
                    isUpdating={updateStatusMutation.isPending}
                  />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {formatDate(feedback.createdAt.toString())}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedFeedback(feedback)}
                    className="flex items-center gap-1"
                  >
                    <Eye className="h-3 w-3" />
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex justify-center gap-2">
        <Button
          variant="outline"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
        >
          Previous
        </Button>
        <span className="py-2 px-4">
          Page {page} of {data?.totalPages ?? 1}
        </span>
        <Button
          variant="outline"
          onClick={() => setPage(p => Math.min(data?.totalPages ?? 1, p + 1))}
          disabled={page === (data?.totalPages ?? 1) || isLoading}
        >
          Next
        </Button>
      </div>

      {/* Feedback Detail Dialog */}
      <Dialog open={!!selectedFeedback} onOpenChange={(open) => !open && setSelectedFeedback(null)}>
        <DialogContent className="max-w-2xl !max-w-2xl w-full min-w-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Feedback Details
            </DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <div className="space-y-4 w-full min-w-0">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="break-words">
                  <span className="font-medium">From:</span> {selectedFeedback.user.username}
                </div>
                <div className="break-words">
                  <span className="font-medium">Email:</span> {selectedFeedback.user.email}
                </div>
                <div>
                  <span className="font-medium">Status:</span>{" "}
                  <Badge variant={selectedFeedback.status === 'pending' ? 'default' : 'secondary'}>
                    {selectedFeedback.status}
                  </Badge>
                </div>
                <div>
                  <span className="font-medium">Created:</span> {formatDate(selectedFeedback.createdAt.toString())}
                </div>
              </div>
              <div className="w-full min-w-0">
                <span className="font-medium text-sm">Message:</span>
                <div className="mt-2 p-3 bg-muted rounded-lg whitespace-pre-wrap break-words max-h-60 overflow-y-auto w-full min-w-0 word-break-all">
                  {selectedFeedback.message}
                </div>
              </div>
              <div className="flex justify-end">
                <StatusCell 
                  feedback={selectedFeedback} 
                  onStatusChange={handleStatusChange}
                  isUpdating={updateStatusMutation.isPending}
                  showLabel={true}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Status Cell Component for editing feedback status
function StatusCell({ 
  feedback, 
  onStatusChange, 
  isUpdating, 
  showLabel = false 
}: { 
  feedback: FeedbackItem; 
  onStatusChange: (id: number, status: "pending" | "reviewed") => void;
  isUpdating: boolean;
  showLabel?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  
  const handleStatusChange = (newStatus: string) => {
    if (newStatus === "pending" || newStatus === "reviewed") {
      onStatusChange(feedback.id, newStatus);
      setIsEditing(false);
    }
  };
  
  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        {showLabel && <span className="text-sm font-medium">Status:</span>}
        <Select
          value={feedback.status}
          onValueChange={handleStatusChange}
          disabled={isUpdating}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsEditing(false)}
          disabled={isUpdating}
        >
          Cancel
        </Button>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2">
      {showLabel && <span className="text-sm font-medium">Status:</span>}
      <Badge variant={feedback.status === 'pending' ? 'default' : 'secondary'}>
        {feedback.status}
      </Badge>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsEditing(true)}
        disabled={isUpdating}
        className="h-6 w-6 p-0"
      >
        <Edit className="h-3 w-3" />
      </Button>
    </div>
  );
}